import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import dayjs from '../../util/dayjs';
import usePermissions from '../../hooks/usePermissions';
import { getAllProfiles } from '../../services/auth';
import {
  fetchNotesByDate,
  fetchAllNotesByDate,
  createNote,
  updateNote,
  deleteNote,
} from '../../services/advisorNotes';
import RichTextEditor from './RichTextEditor';
import { toast } from 'react-toastify';

export default function AdvisorNotesDrawer({ open: controlledOpen, onToggle }) {
  const { session } = useSelector((state) => state.auth);
  const userId = session?.user?.id;
  const { isAdmin } = usePermissions();

  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onToggle || setInternalOpen;
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Editor state
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editorContent, setEditorContent] = useState('');

  // Admin: filter by advisor
  const [advisors, setAdvisors] = useState([]);
  const [filterAdvisorId, setFilterAdvisorId] = useState('');

  // Confirm delete
  const [deletingId, setDeletingId] = useState(null);

  // Alert scheduling state
  const [hasAlert, setHasAlert] = useState(false);
  const [alertTime, setAlertTime] = useState('');
  const [alertSound, setAlertSound] = useState(true);
  const [alertWhatsapp, setAlertWhatsapp] = useState(false);
  const [alertEmail, setAlertEmail] = useState(false);
  const [alertSpeech, setAlertSpeech] = useState(false);

  // Ref for scroll
  const listRef = useRef(null);

  // Load advisors for admin filter
  useEffect(() => {
    if (isAdmin) {
      getAllProfiles().then(({ data }) => {
        if (data) setAdvisors(data.filter((p) => p.is_active !== false));
      });
    }
  }, [isAdmin]);

  // Load notes when date or filter changes
  const loadNotes = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      let result;
      if (isAdmin && !filterAdvisorId) {
        result = await fetchAllNotesByDate(selectedDate);
      } else {
        const targetId = filterAdvisorId || userId;
        result = await fetchNotesByDate(targetId, selectedDate);
      }
      if (result.error) throw result.error;
      setNotes(result.data || []);
    } catch (err) {
      console.error('Error loading notes:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, selectedDate, isAdmin, filterAdvisorId]);

  useEffect(() => {
    if (open) loadNotes();
  }, [open, loadNotes]);

  // Date navigation
  const goToDate = (offset) => {
    setSelectedDate((prev) => dayjs(prev).add(offset, 'day').format('YYYY-MM-DD'));
  };
  const goToToday = () => setSelectedDate(dayjs().format('YYYY-MM-DD'));
  const isToday = selectedDate === dayjs().format('YYYY-MM-DD');

  // CRUD
  const handleCreate = async () => {
    if (!editorContent.trim() || editorContent === '<br>') return;
    try {
      const { data, error } = await createNote({
        userId,
        content: editorContent,
        noteDate: selectedDate,
        alertTime: hasAlert && alertTime ? new Date(alertTime).toISOString() : null,
        alertSound: hasAlert ? alertSound : false,
        alertWhatsapp: hasAlert ? alertWhatsapp : false,
        alertEmail: hasAlert ? alertEmail : false,
        alertSpeech: hasAlert ? alertSpeech : false,
      });
      if (error) throw error;
      setNotes((prev) => [data, ...prev]);
      setEditorContent('');
      setIsCreating(false);
      toast.success('Nota guardada');
    } catch (err) {
      console.error(err);
      toast.error('Error al guardar la nota');
    }
  };

  const handleUpdate = async () => {
    if (!editorContent.trim() || !editingId) return;
    try {
      const { data, error } = await updateNote(editingId, {
        content: editorContent,
        alertTime: hasAlert && alertTime ? new Date(alertTime).toISOString() : null,
        alertSound: hasAlert ? alertSound : false,
        alertWhatsapp: hasAlert ? alertWhatsapp : false,
        alertEmail: hasAlert ? alertEmail : false,
        alertSpeech: hasAlert ? alertSpeech : false,
        alertStatus: 'pending',
      });
      if (error) throw error;
      setNotes((prev) => prev.map((n) => (n.id === editingId ? data : n)));
      setEditingId(null);
      setEditorContent('');
      toast.success('Nota actualizada');
    } catch (err) {
      console.error(err);
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (id) => {
    try {
      const { error } = await deleteNote(id);
      if (error) throw error;
      setNotes((prev) => prev.filter((n) => n.id !== id));
      setDeletingId(null);
      toast.success('Nota eliminada');
    } catch (err) {
      console.error(err);
      toast.error('Error al eliminar');
    }
  };

  const startEditing = (note) => {
    setEditingId(note.id);
    setEditorContent(note.content);
    setIsCreating(false);
    
    // Load alerts state from note
    setHasAlert(!!note.alert_time);
    setAlertTime(note.alert_time ? dayjs(note.alert_time).format('YYYY-MM-DDTHH:mm') : '');
    setAlertSound(note.alert_sound ?? false);
    setAlertWhatsapp(note.alert_whatsapp ?? false);
    setAlertEmail(note.alert_email ?? false);
    setAlertSpeech(note.alert_speech ?? false);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setIsCreating(false);
    setEditorContent('');
    setHasAlert(false);
    setAlertSpeech(false);
  };

  const startCreating = () => {
    setIsCreating(true);
    setEditingId(null);
    setEditorContent('');
    
    // Default alert settings
    setHasAlert(false);
    setAlertTime(dayjs().add(1, 'hour').format('YYYY-MM-DDTHH:mm'));
    setAlertSound(true);
    setAlertWhatsapp(false);
    setAlertEmail(false);
    setAlertSpeech(false);
  };

  // Format display date
  const displayDate = dayjs(selectedDate);
  const dateLabel = isToday
    ? 'Hoy'
    : displayDate.format('YYYY-MM-DD') === dayjs().subtract(1, 'day').format('YYYY-MM-DD')
    ? 'Ayer'
    : displayDate.format('ddd, D MMM');

  if (!userId) return null;

  return createPortal(
    <>
      {/* ─── Drawer Backdrop ─── */}
      {open && (
        <div
          className="fixed inset-0 z-[9990] bg-black/20 backdrop-blur-[2px] transition-opacity duration-300"
          onClick={() => { setOpen(false); cancelEditing(); }}
        />
      )}

      {/* ─── Drawer Panel ─── */}
      <div
        className={`
          fixed top-0 right-0 z-[9991]
          h-full w-full sm:w-[420px]
          bg-white shadow-2xl
          flex flex-col
          transform transition-transform duration-300 ease-[cubic-bezier(.22,1,.36,1)]
          ${open ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        {/* ─── Header ─── */}
        <div className="bg-gradient-to-r from-[#0B357F] to-[#1a4e9e] px-5 py-4 text-white shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur-sm">
                <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                  edit_note
                </span>
              </div>
              <div>
                <h2 className="text-base font-extrabold tracking-tight leading-tight">Mis Notas</h2>
                <p className="text-[10px] text-blue-200/70 font-medium uppercase tracking-widest">Diario del asesor</p>
              </div>
            </div>
            <button
              onClick={() => { setOpen(false); cancelEditing(); }}
              className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>

          {/* Date Navigation */}
          <div className="flex items-center justify-between bg-white/10 rounded-xl px-3 py-2 backdrop-blur-sm">
            <button
              onClick={() => goToDate(-1)}
              className="w-7 h-7 rounded-lg hover:bg-white/15 flex items-center justify-center transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_left</span>
            </button>

            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-[14px] text-blue-200/70">calendar_today</span>
              <button
                onClick={goToToday}
                className={`text-sm font-bold ${isToday ? 'text-amber-300' : 'text-white hover:text-amber-200'} transition-colors`}
              >
                {dateLabel}
              </button>
              <span className="text-[10px] text-blue-200/50 font-medium">
                {displayDate.format('DD/MM/YYYY')}
              </span>
            </div>

            <button
              onClick={() => goToDate(1)}
              className="w-7 h-7 rounded-lg hover:bg-white/15 flex items-center justify-center transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_right</span>
            </button>
          </div>

          {/* Admin advisor filter */}
          {isAdmin && (
            <div className="mt-2">
              <select
                value={filterAdvisorId}
                onChange={(e) => setFilterAdvisorId(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-xs text-white placeholder-blue-200/50 outline-none appearance-none cursor-pointer hover:bg-white/15 transition-colors"
              >
                <option value="" className="text-slate-800">Todos los asesores</option>
                {advisors.map((a) => (
                  <option key={a.id} value={a.id} className="text-slate-800">
                    {a.first_name} {a.last_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* ─── Notes List ─── */}
        <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50/50">
          {loading ? (
            // Skeleton
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl p-4 border border-slate-100 animate-pulse">
                  <div className="h-3 bg-slate-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-slate-100 rounded w-1/2 mb-3" />
                  <div className="h-2 bg-slate-100 rounded w-1/4" />
                </div>
              ))}
            </div>
          ) : notes.length === 0 && !isCreating ? (
            // Empty state
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-[32px] text-slate-300" style={{ fontVariationSettings: "'FILL' 1" }}>
                  note_stack
                </span>
              </div>
              <h3 className="text-sm font-bold text-slate-500 mb-1">Sin notas para este día</h3>
              <p className="text-xs text-slate-400 max-w-[220px]">
                Registra recordatorios, observaciones o pendientes del día.
              </p>
            </div>
          ) : (
            <>
              {notes.map((note) => (
                <div key={note.id} className="group">
                  {editingId === note.id ? (
                    // Inline edit mode
                    <div className="bg-white rounded-xl border-2 border-[#0B357F]/20 p-3 shadow-sm dash-scale-in">
                      <RichTextEditor
                        value={editorContent}
                        onChange={setEditorContent}
                        autoFocus
                        minHeight="100px"
                      />
                      
                      {/* Reminder Scheduling */}
                      <div className="mt-3 border-t border-slate-100 pt-2.5">
                        <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-600 hover:text-slate-800">
                          <input
                            type="checkbox"
                            checked={hasAlert}
                            onChange={(e) => setHasAlert(e.target.checked)}
                            className="w-4 h-4 rounded text-[#0B357F] border-slate-300 focus:ring-[#0B357F]"
                          />
                          <span>🔔 Programar recordatorio</span>
                        </label>
                        
                        {hasAlert && (
                          <div className="mt-2 pl-6 space-y-2.5 transition-all duration-200">
                            <div>
                              <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                                Fecha y hora de la alerta
                              </label>
                              <input
                                type="datetime-local"
                                value={alertTime}
                                onChange={(e) => setAlertTime(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 outline-none focus:border-[#0B357F] transition-colors"
                              />
                            </div>
                            
                            <div className="flex flex-wrap gap-x-4 gap-y-2">
                              <label className="flex items-center gap-1.5 cursor-pointer text-[11px] font-semibold text-slate-600 hover:text-slate-800">
                                <input
                                  type="checkbox"
                                  checked={alertSound}
                                  onChange={(e) => setAlertSound(e.target.checked)}
                                  className="w-3.5 h-3.5 rounded text-[#0B357F] border-slate-300 focus:ring-[#0B357F]"
                                />
                                <span>Web/Sonido</span>
                              </label>
                              
                              <label className="flex items-center gap-1.5 cursor-pointer text-[11px] font-semibold text-slate-600 hover:text-slate-800">
                                <input
                                  type="checkbox"
                                  checked={alertWhatsapp}
                                  onChange={(e) => setAlertWhatsapp(e.target.checked)}
                                  className="w-3.5 h-3.5 rounded text-[#0B357F] border-slate-300 focus:ring-[#0B357F]"
                                />
                                <span>WhatsApp</span>
                              </label>
                              
                              <label className="flex items-center gap-1.5 cursor-pointer text-[11px] font-semibold text-slate-600 hover:text-slate-800">
                                <input
                                  type="checkbox"
                                  checked={alertEmail}
                                  onChange={(e) => setAlertEmail(e.target.checked)}
                                  className="w-3.5 h-3.5 rounded text-[#0B357F] border-slate-300 focus:ring-[#0B357F]"
                                />
                                <span>Correo</span>
                              </label>
                              
                              <label className="flex items-center gap-1.5 cursor-pointer text-[11px] font-semibold text-slate-600 hover:text-slate-800">
                                <input
                                  type="checkbox"
                                  checked={alertSpeech}
                                  onChange={(e) => setAlertSpeech(e.target.checked)}
                                  className="w-3.5 h-3.5 rounded text-[#0B357F] border-slate-300 focus:ring-[#0B357F]"
                                />
                                <span>Voz (IA)</span>
                              </label>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-end gap-2 mt-3">
                        <button
                          onClick={cancelEditing}
                          className="px-3 py-1.5 text-xs font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={handleUpdate}
                          disabled={!editorContent.trim() || (hasAlert && !alertTime)}
                          className="px-4 py-1.5 text-xs font-bold text-white bg-[#0B357F] hover:bg-[#1a4e9e] disabled:opacity-40 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors flex items-center gap-1.5"
                        >
                          <span className="material-symbols-outlined text-[14px]">check</span>
                          Guardar
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Note card
                    <div className="bg-white rounded-xl border border-slate-100 hover:border-slate-200 p-4 shadow-sm hover:shadow-md transition-all duration-200 relative">
                      {/* Author + time row */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#0B357F] to-[#3C50E0] flex items-center justify-center text-[10px] font-bold text-white shadow-sm">
                            {(note.profiles?.first_name || '?')[0]}
                          </div>
                          <span className="text-[11px] font-bold text-slate-600">
                            {note.profiles?.first_name} {note.profiles?.last_name}
                          </span>
                        </div>
                        <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                          <span className="material-symbols-outlined text-[11px]">schedule</span>
                          {dayjs(note.created_at).format('h:mm A')}
                        </span>
                      </div>

                      {/* Content */}
                      <div
                        className="text-sm text-slate-700 leading-relaxed note-content"
                        dangerouslySetInnerHTML={{ __html: note.content }}
                      />

                      {/* Alert Badge / Indicators */}
                      {note.alert_time && (
                        <div className="mt-3 flex items-center justify-between border-t border-slate-50 pt-2 text-[10px]">
                          <div className="flex items-center gap-1.5 text-slate-500 font-semibold">
                            <span className="material-symbols-outlined text-[12px] text-[#0B357F]">notifications_active</span>
                            <span>
                              {dayjs(note.alert_time).format('DD/MM h:mm A')}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                              note.alert_status === 'sent' 
                                ? 'bg-green-50 text-green-600 border border-green-200' 
                                : note.alert_status === 'failed' 
                                ? 'bg-red-50 text-red-600 border border-red-200' 
                                : 'bg-amber-50 text-amber-600 border border-amber-200'
                            }`}>
                              {note.alert_status === 'sent' 
                                ? 'Enviado' 
                                : note.alert_status === 'failed' 
                                ? 'Fallido' 
                                : 'Pendiente'}
                            </span>
                            
                            <div className="flex items-center gap-1 text-slate-400">
                              {note.alert_sound && (
                                <span className="material-symbols-outlined text-[12px]" title="Alerta Web (Sonido)">volume_up</span>
                              )}
                              {note.alert_whatsapp && (
                                <span className="material-symbols-outlined text-[12px]" title="Alerta WhatsApp">chat</span>
                              )}
                              {note.alert_email && (
                                <span className="material-symbols-outlined text-[12px]" title="Alerta Correo">mail</span>
                              )}
                              {note.alert_speech && (
                                <span className="material-symbols-outlined text-[12px]" title="Voz (IA)">record_voice_over</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Actions — visible on hover */}
                      {(note.user_id === userId || isAdmin) && (
                        <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          {note.user_id === userId && (
                            <button
                              onClick={() => startEditing(note)}
                              className="w-7 h-7 rounded-lg bg-slate-50 hover:bg-blue-50 text-slate-400 hover:text-[#0B357F] flex items-center justify-center transition-colors"
                              title="Editar"
                            >
                              <span className="material-symbols-outlined text-[14px]">edit</span>
                            </button>
                          )}
                          {deletingId === note.id ? (
                            <div className="flex items-center gap-1 bg-red-50 rounded-lg px-1.5 py-0.5">
                              <button
                                onClick={() => handleDelete(note.id)}
                                className="text-[10px] font-bold text-red-600 hover:text-red-700 px-1"
                              >
                                Sí
                              </button>
                              <button
                                onClick={() => setDeletingId(null)}
                                className="text-[10px] font-bold text-slate-500 hover:text-slate-700 px-1"
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeletingId(note.id)}
                              className="w-7 h-7 rounded-lg bg-slate-50 hover:bg-red-50 text-slate-400 hover:text-red-500 flex items-center justify-center transition-colors"
                              title="Eliminar"
                            >
                              <span className="material-symbols-outlined text-[14px]">delete</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {/* Inline create form */}
          {isCreating && (
            <div className="bg-white rounded-xl border-2 border-[#0B357F]/20 p-3 shadow-sm dash-scale-in">
              <div className="flex items-center gap-2 mb-2">
                <span className="material-symbols-outlined text-[14px] text-[#0B357F]" style={{ fontVariationSettings: "'FILL' 1" }}>
                  add_circle
                </span>
                <span className="text-[11px] font-bold text-[#0B357F] uppercase tracking-wider">Nueva nota</span>
              </div>
              <RichTextEditor
                value={editorContent}
                onChange={setEditorContent}
                autoFocus
                minHeight="120px"
                placeholder="Escribe tu nota del día..."
              />

              {/* Reminder Scheduling */}
              <div className="mt-3 border-t border-slate-100 pt-2.5">
                <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-600 hover:text-slate-800">
                  <input
                    type="checkbox"
                    checked={hasAlert}
                    onChange={(e) => setHasAlert(e.target.checked)}
                    className="w-4 h-4 rounded text-[#0B357F] border-slate-300 focus:ring-[#0B357F]"
                  />
                  <span>🔔 Programar recordatorio</span>
                </label>
                
                {hasAlert && (
                  <div className="mt-2 pl-6 space-y-2.5 transition-all duration-200">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                        Fecha y hora de la alerta
                      </label>
                      <input
                        type="datetime-local"
                        value={alertTime}
                        onChange={(e) => setAlertTime(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 outline-none focus:border-[#0B357F] transition-colors"
                      />
                    </div>
                    
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      <label className="flex items-center gap-1.5 cursor-pointer text-[11px] font-semibold text-slate-600 hover:text-slate-800">
                        <input
                          type="checkbox"
                          checked={alertSound}
                          onChange={(e) => setAlertSound(e.target.checked)}
                          className="w-3.5 h-3.5 rounded text-[#0B357F] border-slate-300 focus:ring-[#0B357F]"
                        />
                        <span>Web/Sonido</span>
                      </label>
                      
                      <label className="flex items-center gap-1.5 cursor-pointer text-[11px] font-semibold text-slate-600 hover:text-slate-800">
                        <input
                          type="checkbox"
                          checked={alertWhatsapp}
                          onChange={(e) => setAlertWhatsapp(e.target.checked)}
                          className="w-3.5 h-3.5 rounded text-[#0B357F] border-slate-300 focus:ring-[#0B357F]"
                        />
                        <span>WhatsApp</span>
                      </label>
                      
                      <label className="flex items-center gap-1.5 cursor-pointer text-[11px] font-semibold text-slate-600 hover:text-slate-800">
                        <input
                          type="checkbox"
                          checked={alertEmail}
                          onChange={(e) => setAlertEmail(e.target.checked)}
                          className="w-3.5 h-3.5 rounded text-[#0B357F] border-slate-300 focus:ring-[#0B357F]"
                        />
                        <span>Correo</span>
                      </label>
                      
                      <label className="flex items-center gap-1.5 cursor-pointer text-[11px] font-semibold text-slate-600 hover:text-slate-800">
                        <input
                          type="checkbox"
                          checked={alertSpeech}
                          onChange={(e) => setAlertSpeech(e.target.checked)}
                          className="w-3.5 h-3.5 rounded text-[#0B357F] border-slate-300 focus:ring-[#0B357F]"
                        />
                        <span>Voz (IA)</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 mt-3">
                <button
                  onClick={cancelEditing}
                  className="px-3 py-1.5 text-xs font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!editorContent.trim() || editorContent === '<br>' || (hasAlert && !alertTime)}
                  className="px-4 py-1.5 text-xs font-bold text-white bg-[#0B357F] hover:bg-[#1a4e9e] disabled:opacity-40 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors flex items-center gap-1.5"
                >
                  <span className="material-symbols-outlined text-[14px]">check</span>
                  Guardar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ─── Footer: New note button ─── */}
        {!isCreating && !editingId && (
          <div className="shrink-0 px-4 py-3 border-t border-slate-100 bg-white">
            <button
              onClick={startCreating}
              className="w-full py-2.5 bg-gradient-to-r from-[#0B357F] to-[#1a4e9e] hover:from-[#1a4e9e] hover:to-[#0B357F] text-white text-sm font-bold rounded-xl shadow-md shadow-[#0B357F]/20 hover:shadow-lg hover:shadow-[#0B357F]/30 transition-all duration-300 flex items-center justify-center gap-2 active:scale-[0.98]"
            >
              <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                add_circle
              </span>
              Nueva nota
            </button>
          </div>
        )}
      </div>

      {/* Scoped styles for note content rendering */}
      <style>{`
        .note-content ul { list-style-type: disc; padding-left: 1.5em; }
        .note-content ol { list-style-type: decimal; padding-left: 1.5em; }
        .note-content b, .note-content strong { font-weight: 700; }
        .note-content i, .note-content em { font-style: italic; }
        .note-content u { text-decoration: underline; }
      `}</style>
    </>,
    document.body
  );
}
